// Application Insights configuration for React app
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';

// Create browser history for routing tracking
const browserHistory = createBrowserHistory({ basename: '' });

// Create React plugin instance
const reactPlugin = new ReactPlugin();

// Application Insights configuration
const appInsights = new ApplicationInsights({
  config: {
    // Get connection string from environment variables
    connectionString: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING,
    
    // Alternative: Use instrumentation key (legacy method)
    // instrumentationKey: process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY,
    
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: {
        history: browserHistory,
        // Enable automatic route change tracking
        enableAutoRouteTracking: true,
        // Enable click tracking
        enableClickAnalyticsPlugin: true,
        // Enable crash reporting
        enableUnhandledRejectionTracking: true,
      }
    },
    
    // Additional configuration options
    enableAutoRouteTracking: true, // Auto track route changes
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    enableCorsCorrelation: true,
    enableAutoExceptionTracking: true,
    
    // Performance and sampling settings
    samplingPercentage: 100, // 100% sampling for development, reduce for production
    maxBatchInterval: 15000, // Send telemetry every 15 seconds
    maxBatchSizeInBytes: 65000,
    
    // Privacy settings
    cookieCfg: {
      enabled: true
    },
    
    // Custom properties to add to all telemetry
    addTelemetryInitializer: (envelope) => {
      envelope.tags['ai.application.ver'] = process.env.REACT_APP_VERSION || '1.0.0';
      envelope.tags['ai.cloud.role'] = 'ecpl-web-manager-frontend';
      envelope.tags['ai.cloud.roleInstance'] = 'frontend-instance';
    }
  }
});

// Load Application Insights
appInsights.loadAppInsights();

// Track initial page view
appInsights.trackPageView();

// Export the configured instance and react plugin for use in components
export { appInsights, reactPlugin, browserHistory };

// Helper functions for common telemetry operations
export const trackEvent = (name, properties = {}, measurements = {}) => {
  appInsights.trackEvent({ name }, properties, measurements);
};

export const trackException = (exception, properties = {}) => {
  appInsights.trackException({ exception }, properties);
};

export const trackTrace = (message, severityLevel = 1, properties = {}) => {
  appInsights.trackTrace({ message, severityLevel }, properties);
};

export const trackMetric = (name, average, properties = {}) => {
  appInsights.trackMetric({ name, average }, properties);
};

export const trackDependency = (dependencyTypeName, name, data, duration, success, properties = {}) => {
  appInsights.trackDependencyData({
    dependencyTypeName,
    name,
    data,
    duration,
    success
  }, properties);
};

// Set user context
export const setUserContext = (userId, accountId = null, userProperties = {}) => {
  appInsights.setAuthenticatedUserContext(userId, accountId);
  appInsights.addTelemetryInitializer((envelope) => {
    envelope.tags['ai.user.id'] = userId;
    if (accountId) {
      envelope.tags['ai.user.accountId'] = accountId;
    }
    // Add custom user properties
    Object.keys(userProperties).forEach(key => {
      envelope.data[key] = userProperties[key];
    });
  });
};

// Clear user context (e.g., on logout)
export const clearUserContext = () => {
  appInsights.clearAuthenticatedUserContext();
};